import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CloseIcon from "@mui/icons-material/Close";
import colors from "../../Constants/colors";
import { useUsage } from "../../Query/useUsage";
import { UsageRow } from "../../Models/usage";
import { buildPeriod, formatTokens, formatUsd } from "../../Helpers/usageWindow";
import UsageWidget from "./UsageWidget";
import UsagePeriodSelector from "./UsagePeriodSelector";

type OverviewGroupBy = "user" | "workspace";

interface GroupConfig {
  idField: keyof UsageRow;
  nameField: keyof UsageRow;
  // Shown when the row has no id at all (usage not tied to this dimension).
  emptyKey: string;
  // Shown when the id survives but the referenced record is gone.
  fallbackKey: string;
  toggleKey: string;
}

const GROUPS: Record<OverviewGroupBy, GroupConfig> = {
  user: {
    idField: "user_id",
    nameField: "user_email",
    emptyKey: "deletedUser",
    fallbackKey: "deletedUser",
    toggleKey: "byUser",
  },
  workspace: {
    idField: "workspace_id",
    nameField: "workspace",
    emptyKey: "noWorkspace",
    fallbackKey: "deletedWorkspace",
    toggleKey: "byWorkspace",
  },
};

interface Selection {
  type: OverviewGroupBy;
  id: number;
  label: string;
}

const Usage = () => {
  const { t } = useTranslation("usage");

  const [groupBy, setGroupBy] = useState<OverviewGroupBy>("user");
  const [range, setRange] = useState<{ from?: string; to?: string }>(() =>
    buildPeriod("thisMonth"),
  );
  const [selection, setSelection] = useState<Selection | null>(null);

  const params = useMemo(
    () => ({ group_by: groupBy, from: range.from, to: range.to }),
    [groupBy, range.from, range.to],
  );

  const { data, isLoading } = useUsage(params);

  const rows: UsageRow[] = data ?? [];

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          input: acc.input + (r.input_tokens ?? 0),
          cached: acc.cached + (r.cached_input_tokens ?? 0),
          output: acc.output + (r.output_tokens ?? 0),
          reasoning: acc.reasoning + (r.reasoning_tokens ?? 0),
          tokens: acc.tokens + (r.tokens_total ?? 0),
          cost: acc.cost + (r.cost_total ?? 0),
        }),
        {
          input: 0,
          cached: 0,
          output: 0,
          reasoning: 0,
          tokens: 0,
          cost: 0,
        },
      ),
    [rows],
  );

  const headerCellSx = {
    fontWeight: 600,
    color: colors.gray700,
    backgroundColor: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: "0.8rem",
    whiteSpace: "nowrap" as const,
  };

  const bodyCellSx = {
    color: colors.black,
    fontSize: "0.85rem",
    borderBottom: `1px solid ${colors.gray100}`,
  };

  const totalCellSx = {
    ...bodyCellSx,
    fontWeight: 700,
    backgroundColor: colors.gray50,
    borderTop: `2px solid ${colors.gray200}`,
  };

  // The overview always mixes chat and evaluation usage, so it shows only the
  // columns both sources share. Source-specific metrics live in the details.
  const columnCount = 7;

  const group = GROUPS[groupBy];

  const rowId = (row: UsageRow): number | null | undefined =>
    row[group.idField] as number | null | undefined;

  const rowLabel = (row: UsageRow): string => {
    const name = row[group.nameField] as string;
    if (name) return name;
    const id = rowId(row);
    return id === null || id === undefined
      ? t(group.emptyKey)
      : t(group.fallbackKey);
  };

  const handleGroupChange = (value: OverviewGroupBy | null) => {
    if (!value) return;
    setGroupBy(value);
    setSelection(null);
  };

  const handleRowClick = (row: UsageRow) => {
    const id = rowId(row);
    if (id === null || id === undefined) return;
    setSelection({ type: groupBy, id, label: rowLabel(row) });
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}>
      <Box sx={{ maxWidth: "1280px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.black}`,
            background: colors.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <QueryStatsIcon
              sx={{ fontSize: 32, color: colors.black }}
            />
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: colors.black }}
              >
                {t("title")}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.gray500 }}>
                {t("subtitle")}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.black}`,
            background: colors.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={groupBy}
              onChange={(_, value) => handleGroupChange(value)}
              sx={{
                "& .MuiToggleButton-root.Mui-selected": {
                  backgroundColor: colors.accent,
                  color: colors.black,
                  "&:hover": { backgroundColor: colors.accent },
                },
              }}
            >
              {(Object.keys(GROUPS) as OverviewGroupBy[]).map((value) => (
                <ToggleButton key={value} value={value}>
                  {t(GROUPS[value].toggleKey)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <UsagePeriodSelector defaultPreset="thisMonth" onChange={setRange} />
          </Stack>

          <TableContainer
            sx={{
              border: `1px solid ${colors.gray200}`,
              borderRadius: 2,
              overflow: "auto",
              position: "relative",
              minHeight: 200,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>{t(groupBy)}</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("inputTokens")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("cachedTokens")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("outputTokens")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("reasoningTokens")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("totalTokens")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("cost")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255,255,255,0.6)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress
                      size={32}
                      sx={{ color: colors.accent }}
                    />
                  </Box>
                )}
                {rows.length > 0
                  ? rows.map((row, index) => {
                      const id = rowId(row);
                      const clickable = id !== null && id !== undefined;
                      const isSelected =
                        clickable &&
                        selection?.type === groupBy &&
                        selection?.id === id;
                      return (
                        <TableRow
                          key={`${groupBy}-${id ?? "none"}-${index}`}
                          onClick={() => handleRowClick(row)}
                          sx={{
                            cursor: clickable ? "pointer" : "default",
                            transition: "background-color 0.15s ease-in-out",
                            backgroundColor: isSelected
                              ? `${colors.accent}22`
                              : undefined,
                            "&:hover": clickable
                              ? {
                                  backgroundColor: `${colors.accent}14`,
                                }
                              : undefined,
                          }}
                        >
                          <TableCell sx={bodyCellSx}>
                            {rowLabel(row)}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {formatTokens(row.input_tokens)}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {formatTokens(row.cached_input_tokens)}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {formatTokens(row.output_tokens)}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {formatTokens(row.reasoning_tokens)}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {formatTokens(row.tokens_total)}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {formatUsd(row.cost_total)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  : !isLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={columnCount}
                          align="center"
                          sx={{
                            color: colors.gray500,
                            py: 6,
                            backgroundColor: colors.gray50,
                            fontSize: "0.875rem",
                          }}
                        >
                          {t("noData")}
                        </TableCell>
                      </TableRow>
                    )}
                {rows.length > 0 && (
                  <TableRow>
                    <TableCell sx={totalCellSx}>{t("total")}</TableCell>
                    <TableCell sx={totalCellSx} align="right">
                      {formatTokens(totals.input)}
                    </TableCell>
                    <TableCell sx={totalCellSx} align="right">
                      {formatTokens(totals.cached)}
                    </TableCell>
                    <TableCell sx={totalCellSx} align="right">
                      {formatTokens(totals.output)}
                    </TableCell>
                    <TableCell sx={totalCellSx} align="right">
                      {formatTokens(totals.reasoning)}
                    </TableCell>
                    <TableCell sx={totalCellSx} align="right">
                      {formatTokens(totals.tokens)}
                    </TableCell>
                    <TableCell sx={totalCellSx} align="right">
                      {formatUsd(totals.cost)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1, color: colors.gray500 }}
          >
            {t("drillHint")}
          </Typography>

          {selection && (
            <Box sx={{ mt: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: colors.black }}
                >
                  {t("detailsFor", { name: selection.label })}
                </Typography>
                <IconButton size="small" onClick={() => setSelection(null)}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
              <UsageWidget
                key={`${selection.type}-${selection.id}`}
                userId={selection.type === "user" ? selection.id : undefined}
                workspaceId={
                  selection.type === "workspace" ? selection.id : undefined
                }
                rangeControlled
                from={range.from}
                to={range.to}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Usage;
